'use client';

import { Button, Input, Label, Spinner } from '@monorepo/ui';
import { cn } from '@monorepo/utils';
import * as React from 'react';

interface UserAuthFormProps extends React.HTMLAttributes<HTMLDivElement> {
  handleSubmit: (options: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    address_1?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
    phone?: string;
    title?: string;
    birth_date?: string;
  }) => Promise<void>;
  type?: 'register' | 'login';
}

export function UserAuthForm({
  className,
  handleSubmit,
  type = 'login',
  children,
  ...props
}: UserAuthFormProps) {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  async function onSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.target as HTMLFormElement);
    const submittedEmail = formData.get('email');
    const submittedPassword = formData.get('password');
    const submittedFirstName = formData.get('firstName');
    const submittedLastName = formData.get('lastName');
    const submittedAddress_1 = formData.get('address_1');
    const submittedCity = formData.get('city');
    const submittedState = formData.get('state');
    const submittedPostcode = formData.get('postcode');
    const submittedCountry = formData.get('country');
    const submittedPhone = formData.get('phone');
    const submittedTitle = formData.get('title');
    const submittedBirth_date = formData.get('birth_date');

    try {
      setError(null);

      await handleSubmit({
        email: submittedEmail as string,
        password: submittedPassword as string,
        firstName: submittedFirstName as string,
        lastName: submittedLastName as string,
        address_1: submittedAddress_1 as string,
        city: submittedCity as string,
        state: submittedState as string,
        postcode: submittedPostcode as string,
        country: submittedCountry as string,
        phone: submittedPhone as string,
        title: submittedTitle as string,
        birth_date: submittedBirth_date as string,
      });
    } catch (error) {
      setError('Ungültige Email oder Passwort');
    }

    setIsLoading(false);
  }

  return (
    <div className={cn('grid gap-6', className)} {...props}>
      <form onSubmit={onSubmit}>
        <div className="grid gap-2">
          <div className="grid gap-1 p-1">
            <Label className="sr-only" htmlFor="email">
              Email
            </Label>
            <Input
              id="email"
              placeholder="Email Adresse eingeben"
              type="email"
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect="off"
              name="email"
              disabled={isLoading}
              required
              className={cn(error ? 'border-red-500' : '')}
            />

            <Input
              id="password"
              placeholder="Passwort eingeben"
              type="password"
              autoCapitalize="none"
              autoComplete="password"
              autoCorrect="off"
              name="password"
              disabled={isLoading}
              required
              className={cn(error ? 'border-red-500' : '')}
            />

            {type === 'register' ? (
              <>
                <Input
                  id="Vorname"
                  placeholder="Vorname eingeben"
                  type="text"
                  autoCorrect="off"
                  name="firstName"
                  required
                />

                <Input
                  id="Nachname"
                  placeholder="Nachname eingeben"
                  type="text"
                  autoCorrect="off"
                  name="lastName"
                  required
                />

                <Input
                  id="Adresse"
                  placeholder="Adresse eingeben"
                  type="text"
                  autoCorrect="off"
                  name="address_1"
                  required
                />

                <Input
                  id="Stadt"
                  placeholder="Stadt eingeben"
                  type="text"
                  autoCorrect="off"
                  name="city"
                  required
                />

                <Input
                  id="Bundesland"
                  placeholder="Bundesland eingeben"
                  type="text"
                  autoCorrect="off"
                  name="state"
                  required
                />

                <Input
                  id="Postleitzahl"
                  placeholder="Postleitzahl eingeben"
                  type="text"
                  autoCorrect="off"
                  name="postcode"
                  required
                />

                <Input
                  id="Land"
                  placeholder="Land eingeben"
                  type="text"
                  autoCorrect="off"
                  name="country"
                  required
                />

                <Input
                  id="Telefonnummer"
                  placeholder="Telefonnummer eingeben"
                  type="text"
                  autoCorrect="off"
                  name="phone"
                  required
                />

                <Input
                  id="Titel"
                  placeholder="Titel eingeben"
                  type="text"
                  autoCorrect="off"
                  name="title"
                  required
                />

                <Input
                  id="Geburtsdatum"
                  placeholder="Geburtsdatum eingeben"
                  type="date"
                  autoCorrect="off"
                  name="birth_date"
                  required
                />
              </>
            ) : null}
          </div>

          {error && <div className="text-red-500 text-xs pb-2">{error}</div>}

          <Button id="login-button" disabled={isLoading}>
            {isLoading && <Spinner className="mr-2 h-4 w-4 animate-spin" />}
            {type === 'login' ? 'Mit Email anmelden' : 'Registrieren'}
          </Button>
        </div>
      </form>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
      </div>
    </div>
  );
}
